// The app's data, generated from .appfactory/product-plan.json.
//
// Regenerated by `appfactory product apply`. Edit the PLAN and re-apply
// rather than editing here, or the next apply will disagree with you.
//
// Deliberately the same shape the chassis shipped: plain `ref`s in a module,
// seeded through `demoData` so /demo shows rows and the real app starts
// empty. A generated app should not introduce a state library its author
// never asked for — and `demoData` is what makes the QA `empty` fixture
// actually produce an empty screen rather than a populated one.

import { ref, computed, watch, toRaw } from 'vue';
import { demoData } from './core/demo.js';
import { records } from './core/storage.js';

// Rows survive a reload.
//
// `core/storage.js` is an IndexedDB record store the chassis has always
// shipped, and the generated store used to ignore it completely — a plain
// `ref([])` that lost everything the moment the page reloaded. An app that
// cannot keep what you typed into it is not working, however well it renders.
//
// Demo rows are NOT persisted. They have negative ids and exist for /demo and
// for QA fixtures; writing them to the real store would put sample customers
// in somebody's actual queue.
const persist = (row) => (Number(row?.id) > 0 ? records.put(toRaw(row)) : null);

// ONE id sequence for every entity, because there is one store.
//
// `records` is a single IndexedDB object store keyed on `id`. Giving each
// entity its own counter starting at 1 meant the first order written
// overwrote the first customer, and removeOrder(1) deleted them — silent
// cross-entity data loss that only appears once an app has two entities.
//
// Seeded from the highest id already stored, so it also survives a reload:
// restarting at 1 would overwrite the first row ever added.
let seq = 0;
const nextId = () => (seq += 1);

// Everything hydrates from ONE read, and nothing may allocate an id before
// it finishes. A row added while this was still in flight took id 1,
// overwrote a stored record, and was then dropped from the screen when the
// hydrated rows replaced the list.
const ready = records.all().then((rows) => {
  seq = rows.reduce((n, r) => Math.max(n, Number(r.id) || 0), 0);
  return rows;
}).catch(() => []);   // no IndexedDB (SSR, private mode) — stay in memory

/** Resolves once stored rows are loaded. Await it before writing. */
export const storeReady = ready;

/** Something a child has written. The English text is the record; the footprints are drawn from it, never stored as glyphs. */
export const messages = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededMessage = demoData([
  { id: -1, title: 'Message 1', text: 'A short text for Message 1, long enough to show wrapping.', surface: 'sandpit', script: 'theropoda', createdAt: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString(), updatedAt: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString() },
  { id: -2, title: 'Message 2', text: 'A short text for Message 2, long enough to show wrapping.', surface: 'scroll', script: 'transitional', createdAt: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString(), updatedAt: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString() },
  { id: -3, title: 'Message 3', text: 'A short text for Message 3, long enough to show wrapping.', surface: 'sandpit', script: 'sawtooth', createdAt: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString(), updatedAt: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString() },
]);
watch(seededMessage, (rows) => {
  if (rows.length && !messages.value.length) messages.value = [...rows];
}, { immediate: true });

export const messagesReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'message');
  if (mine.length) messages.value = mine;
  return messages.value;
});

export function addMessage(input = {}) {
  const row = { id: nextId(), entity: 'message',
    title: input.title ?? '',
    text: input.text ?? '',
    surface: input.surface ?? '',
    script: input.script ?? '',
    createdAt: input.createdAt ?? new Date().toISOString(),
    updatedAt: input.updatedAt ?? new Date().toISOString(),
  };
  messages.value = [row, ...messages.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveMessage(row) {
  messages.value = messages.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeMessage(id) {
  messages.value = messages.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findMessage = (id) =>
  messages.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentMessage = computed(() => messages.value.slice(0, 3));
export const messagesTitle = (row) => row?.title;

/** One character of one footprint alphabet. Stored as data rather than baked into drawing code so the table can be corrected in one place when the reference is read properly. Most characters are two prints — stacked or side by side — which is why layout and a second bearing are fields rather than assumptions. */
export const alphabetLetters = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededAlphabetLetter = demoData([
  { id: -1, script: 'theropoda', character: 'Sample character', layout: 'single', bearing: 1, bearingTwo: 1, isStar: true, kind: 'letter' },
  { id: -2, script: 'transitional', character: 'Sample character', layout: 'stacked', bearing: 2, bearingTwo: 2, isStar: false, kind: 'digit' },
  { id: -3, script: 'sawtooth', character: 'Sample character', layout: 'beside', bearing: 3, bearingTwo: 3, isStar: false, kind: 'mark' },
]);
watch(seededAlphabetLetter, (rows) => {
  if (rows.length && !alphabetLetters.value.length) alphabetLetters.value = [...rows];
}, { immediate: true });

export const alphabetLettersReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'alphabet-letter');
  if (mine.length) alphabetLetters.value = mine;
  return alphabetLetters.value;
});

export function addAlphabetLetter(input = {}) {
  const row = { id: nextId(), entity: 'alphabet-letter',
    script: input.script ?? '',
    character: input.character ?? '',
    layout: input.layout ?? '',
    bearing: input.bearing ?? 0,
    bearingTwo: input.bearingTwo ?? 0,
    isStar: input.isStar ?? false,
    kind: input.kind ?? '',
  };
  alphabetLetters.value = [row, ...alphabetLetters.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveAlphabetLetter(row) {
  alphabetLetters.value = alphabetLetters.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeAlphabetLetter(id) {
  alphabetLetters.value = alphabetLetters.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findAlphabetLetter = (id) =>
  alphabetLetters.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentAlphabetLetter = computed(() => alphabetLetters.value.slice(0, 3));
export const alphabetLettersTitle = (row) => row?.character;

/** How the app behaves. One record. */
export const settings = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededSetting = demoData([
  { id: -1, writingSpeedMs: 1, defaultSurface: 'sandpit', defaultScript: 'theropoda', showLetterHints: true },
  { id: -2, writingSpeedMs: 2, defaultSurface: 'scroll', defaultScript: 'transitional', showLetterHints: false },
  { id: -3, writingSpeedMs: 3, defaultSurface: 'sandpit', defaultScript: 'sawtooth', showLetterHints: false },
]);
watch(seededSetting, (rows) => {
  if (rows.length && !settings.value.length) settings.value = [...rows];
}, { immediate: true });

export const settingsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'setting');
  if (mine.length) settings.value = mine;
  return settings.value;
});

export function addSetting(input = {}) {
  const row = { id: nextId(), entity: 'setting',
    writingSpeedMs: input.writingSpeedMs ?? 0,
    defaultSurface: input.defaultSurface ?? '',
    defaultScript: input.defaultScript ?? '',
    showLetterHints: input.showLetterHints ?? false,
  };
  settings.value = [row, ...settings.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveSetting(row) {
  settings.value = settings.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeSetting(id) {
  settings.value = settings.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findSetting = (id) =>
  settings.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentSetting = computed(() => settings.value.slice(0, 3));
export const settingsTitle = (row) => row?.id;
